"use client";

import { useState } from "react";
import clsx from "clsx";
import { Checkbox, IconButton, Input, Select, Tooltip } from "@/ui";
import SettingsField from "./SettingsField";
import { BUILT_IN_HEADINGS } from "@lib/settings/headingDefaults";
import { CANDIDATE_CARD_VARIANTS } from "@lib/floats/candidateCardVariants";
import { JOB_CARD_VARIANTS } from "@lib/floats/jobCardVariants";
import ThemePreviewDialog from "./ThemePreviewDialog";
import styles from "./FloatsSubmissions.module.css";

const PREVIEW_CANDIDATE = {
  id: "theme-preview",
  name: "Alex Morgan",
  position_title: "Senior Product Designer",
  highlights: "10+ years shaping consumer products at high-growth startups.",
  avatar:
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23e0e0e0'/%3E%3Ccircle cx='100' cy='80' r='40' fill='%23bdbdbd'/%3E%3Cellipse cx='100' cy='170' rx='60' ry='50' fill='%23bdbdbd'/%3E%3C/svg%3E",
  skills: [
    { id: 1, name: "Figma", type: "tool" },
    { id: 2, name: "Design Systems", type: "tool" },
    { id: 3, name: "Prototyping", type: "tool" },
    { id: 4, name: "User Research", type: "tool" },
    { id: 5, name: "Accessibility", type: "tool" },
  ],
};

const PREVIEW_JOB = {
  id: "theme-preview-job",
  title: "Senior Product Designer",
  company: "Northwind Studio",
  companyLogo: "/img/floats/google-logo.svg",
  description:
    "Shape end-to-end design for new products used by millions of creators worldwide.",
  attributes: [
    { id: 1, label: "Sydney, NSW", emoji: "📍" },
    { id: 2, label: "Full-time", emoji: "💼" },
    { id: 3, label: "Hybrid", emoji: "🏢" },
    { id: 4, label: "$140,000 – $170,000", emoji: "💰" },
  ],
};

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
}

/** Pre-selection counts are small, so a select beats a free-text number field. */
const COUNT_OPTIONS = Array.from({ length: 21 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

const HEADING_FIELDS = [
  { key: "details", label: "Details section" },
  { key: "portfolio", label: "Portfolio section" },
  { key: "skills", label: "Skills section" },
] as const;

const CONTENT_FIELDS = [
  { key: "skills", label: "Skills" },
  { key: "roles", label: "Recent roles" },
  { key: "projects", label: "Projects" },
  { key: "education", label: "Education" },
] as const;

interface Recruiter {
  name?: string;
  logo_url?: string;
  type?: string;
  job_card_type?: string;
  [field: string]: unknown;
}

interface FloatsSubmissionsProps {
  recruiter: Recruiter;
  setRecruiter: (updater: (prev: Recruiter) => Recruiter) => void;
}

function PreSelectionFields({
  scope,
  recruiter,
  setRecruiter,
}: FloatsSubmissionsProps & { scope: "float" | "submission" }) {
  return (
    <div className={styles.fieldGrid}>
      {CONTENT_FIELDS.map(({ key, label }) => {
        const field = `${scope}_initial_${key}`;
        return (
          <Select
            key={field}
            label={label}
            options={COUNT_OPTIONS}
            value={String(recruiter[field] ?? 0)}
            onChange={(value: string) =>
              setRecruiter((prev) => ({ ...prev, [field]: Number(value) }))
            }
          />
        );
      })}
    </div>
  );
}

function HeadingFields({
  scope,
  recruiter,
  setRecruiter,
}: FloatsSubmissionsProps & { scope: "float" | "submission" }) {
  return (
    <div className={styles.headingGrid}>
      {HEADING_FIELDS.map(({ key, label }) => {
        const field = `${scope}_${key}_heading`;
        return (
          <Input
            key={field}
            label={label}
            placeholder={BUILT_IN_HEADINGS[key]}
            value={String(recruiter[field] ?? "")}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setRecruiter((prev) => ({ ...prev, [field]: event.target.value }))
            }
          />
        );
      })}
    </div>
  );
}

/**
 * Settings that control the content of Floats and Submissions. Settings that
 * shape the Float/Submission page header (banner, colours, contact details)
 * stay in the Theme tab.
 */
export default function FloatsSubmissions({
  recruiter,
  setRecruiter,
}: FloatsSubmissionsProps) {
  const [previewType, setPreviewType] = useState<string | null>(null);

  const selectStyle = (field: string, value: string) =>
    setRecruiter((prev) => ({ ...prev, [field]: value }));

  const activateStyle =
    (field: string, type: string) =>
    (event: React.MouseEvent | React.KeyboardEvent) => {
      if (event.type === "keydown") {
        const { key } = event as React.KeyboardEvent;
        if (key !== "Enter" && key !== " ") return;
        event.preventDefault();
      }
      selectStyle(field, type);
    };

  return (
    <>
      <h3 className={styles.sectionHeading}>General settings</h3>

      <SettingsField
        label="Profile card style"
        description="Choose the style of the profile card that appears in emails and in link previews."
      >
        <div className={styles.styleGrid} role="radiogroup">
          {CANDIDATE_CARD_VARIANTS.map(({ type, label, Component }) => (
            <div
              key={type}
              className={clsx(
                styles.styleOption,
                recruiter.type === type && styles.styleOptionSelected,
              )}
              onClick={activateStyle("type", type)}
              onKeyDown={activateStyle("type", type)}
              role="radio"
              tabIndex={0}
              aria-checked={recruiter.type === type}
              aria-label={`${label} card style`}
            >
              <div className={styles.styleScaler}>
                <Component
                  candidate={PREVIEW_CANDIDATE}
                  recruiterLogoUrl={recruiter?.logo_url}
                  recruiterName={recruiter?.name}
                />
              </div>
              <span className={styles.styleLabel}>{label}</span>
              <Tooltip content="Preview email">
                <IconButton
                  size="sm"
                  className={styles.previewButton}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    setPreviewType(type);
                  }}
                  aria-label={`Preview ${label} email`}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </div>
          ))}
        </div>
      </SettingsField>

      <SettingsField
        label="Job card style"
        description="Choose the style of the job card used to present open roles in emails and link previews."
      >
        <div className={styles.styleGrid} role="radiogroup">
          {JOB_CARD_VARIANTS.map(({ type, label, Component }) => (
            <div
              key={type}
              className={clsx(
                styles.styleOption,
                recruiter.job_card_type === type && styles.styleOptionSelected,
              )}
              onClick={activateStyle("job_card_type", type)}
              onKeyDown={activateStyle("job_card_type", type)}
              role="radio"
              tabIndex={0}
              aria-checked={recruiter.job_card_type === type}
              aria-label={`${label} job card style`}
            >
              <div className={styles.styleScaler}>
                <Component
                  job={PREVIEW_JOB}
                  recruiterLogoUrl={recruiter?.logo_url}
                  recruiterName={recruiter?.name}
                />
              </div>
              <span className={styles.styleLabel}>{label}</span>
            </div>
          ))}
        </div>
      </SettingsField>

      <SettingsField label="Content options">
        <div className={styles.checkGroup}>
          <Checkbox
            label="Show a 'Not interested' button for candidates"
            checked={Boolean(recruiter.show_not_interested_button)}
            onChange={(checked: boolean) =>
              setRecruiter((prev) => ({
                ...prev,
                show_not_interested_button: checked,
              }))
            }
          />
          <Checkbox
            label="Disable emojis on skills"
            description="When enabled, the emoji picker on skill chips is hidden."
            checked={Boolean(recruiter.disable_skill_emojis)}
            onChange={(checked: boolean) =>
              setRecruiter((prev) => ({
                ...prev,
                disable_skill_emojis: checked,
              }))
            }
          />
        </div>
      </SettingsField>

      <h3 className={styles.sectionHeading}>Floats</h3>

      {/* Nothing to accept until a terms document exists on the Recruiter tab. */}
      {Boolean(recruiter.terms_document_filename) && (
        <SettingsField label="Terms">
          <Checkbox
            label="Include terms acceptance for CV access on Float - I'm interested action"
            checked={Boolean(recruiter.float_terms_acceptance)}
            onChange={(checked: boolean) =>
              setRecruiter((prev) => ({
                ...prev,
                float_terms_acceptance: checked,
              }))
            }
          />
        </SettingsField>
      )}

      <SettingsField
        label="Pre-selected candidate content"
        description="How much of each candidate's profile is selected for you when you create a Float. Set a value to 0 to select nothing."
      >
        <PreSelectionFields
          scope="float"
          recruiter={recruiter}
          setRecruiter={setRecruiter}
        />
      </SettingsField>

      <SettingsField
        label="Section headings"
        description="The headings a new Float starts with. Leave blank to use the default, and rename them per candidate section while building a Float."
      >
        <HeadingFields
          scope="float"
          recruiter={recruiter}
          setRecruiter={setRecruiter}
        />
      </SettingsField>

      <h3 className={styles.sectionHeading}>Submissions</h3>

      <SettingsField
        label="Pre-selected candidate content"
        description="How much of each candidate's profile is selected for you when you create a Submission. Set a value to 0 to select nothing."
      >
        <PreSelectionFields
          scope="submission"
          recruiter={recruiter}
          setRecruiter={setRecruiter}
        />
      </SettingsField>

      <SettingsField
        label="Section headings"
        description="The headings a new Submission starts with. Leave blank to use the default, and rename them per candidate section while building a Submission."
      >
        <HeadingFields
          scope="submission"
          recruiter={recruiter}
          setRecruiter={setRecruiter}
        />
      </SettingsField>

      <ThemePreviewDialog
        open={Boolean(previewType)}
        themeType={previewType}
        recruiter={recruiter}
        onClose={() => setPreviewType(null)}
      />
    </>
  );
}
